import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import {
  tap,
  catchError,
  finalize,
  shareReplay,
  filter,
  take,
  switchMap,
} from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface RegisterUserData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  phone_number?: string;
  marketing_consent?: boolean;
  recaptchaToken?: string;
}

export interface RegisterOAuthData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  marketing_consent?: boolean;
}

export interface VerifyEmailData {
  email: string;
  code: string;
}

export interface LoginUserData {
  email: string;
  password: string;
  recaptchaToken?: string;
}

export interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  balance?: number;
  is_admin?: boolean;
  marketing_consent?: boolean;
  is_oauth_user?: boolean;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface RefreshTokenResponse {
  status: string;
  token: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl || 'http://localhost:3000/api/v1';
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();
  private userRefreshCache$?: Observable<User | null>;
  private lastUserRefresh = 0;
  private readonly USER_REFRESH_INTERVAL = 5 * 60 * 1000;
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private tokenRefreshTimer: any;

  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_DATA_KEY = 'user_data';

  constructor(private http: HttpClient) {
    this.loadStoredAuth();
    this.startProactiveTokenRefresh();
  }

  private loadStoredAuth(): void {
    const storedToken = localStorage.getItem(this.TOKEN_KEY);
    const storedUserData = localStorage.getItem(this.USER_DATA_KEY);

    if (storedToken) {
      this.tokenSubject.next(storedToken);
      this.isLoggedInSubject.next(true);

      if (storedUserData) {
        try {
          const userData = JSON.parse(storedUserData) as User;
          this.currentUserSubject.next(userData);
        } catch (e) {
          console.error('Error parsing stored user data', e);
          this.clearAuthData();
        }
      }
    }
  }

  private decodeToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      return JSON.parse(atob(payload));
    } catch (e) {
      return null;
    }
  }

  private getTokenExpiryTime(token: string): number | null {
    const decoded = this.decodeToken(token);
    if (decoded && decoded.exp) {
      return decoded.exp * 1000;
    }
    return null;
  }

  private startProactiveTokenRefresh(): void {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
    }

    this.tokenRefreshTimer = setInterval(() => {
      const token = this.getToken();
      if (!token) {
        return;
      }

      const expiryTime = this.getTokenExpiryTime(token);
      if (!expiryTime) {
        return;
      }

      const now = Date.now();
      const timeUntilExpiry = expiryTime - now;
      const refreshThreshold = 5 * 60 * 1000;

      if (timeUntilExpiry < refreshThreshold && timeUntilExpiry > 0) {
        this.refreshToken().subscribe();
      }
    }, 60 * 1000);
  }

  public forceRefreshUser(): Observable<User | null> {
    const now = Date.now();

    if (this.userRefreshCache$ && now - this.lastUserRefresh < 30000) {
      return this.userRefreshCache$;
    }

    if (this.isLoggedIn()) {
      this.lastUserRefresh = now;
      this.userRefreshCache$ = this.http
        .get<User>(`${this.apiUrl}/users/me`)
        .pipe(
          tap((user) => {
            this.updateCurrentUser(user);
          }),
          catchError((error) => {
            console.error('Error fetching current user', error);
            if (error.status === 401 || error.status === 403) {
              this.clearAuthData();
            }
            return throwError(() => new Error('Failed to load user data'));
          }),
          shareReplay(1)
        );
      return this.userRefreshCache$;
    }
    return of(null);
  }

  public storeUserData(user: User): void {
    localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(user));
  }

  public clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_DATA_KEY);
    this.currentUserSubject.next(null);
    this.tokenSubject.next(null);
    this.isLoggedInSubject.next(false);
    this.userRefreshCache$ = undefined;
    this.lastUserRefresh = 0;
    this.isRefreshing = false;
    this.refreshTokenSubject.next(null);
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
    }
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  getCurrentUserValue(): User | null {
    return this.currentUserValue;
  }

  register(userData: RegisterUserData): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this.apiUrl}/auth/register`, userData)
      .pipe(
        catchError((error) => {
          let errorMsg = 'Registration failed';
          if (error.status === 409) {
            errorMsg = 'Email already registered';
          } else if (error.error && error.error.message) {
            errorMsg = error.error.message;
          }
          return throwError(() => new Error(errorMsg));
        })
      );
  }

  registerOAuth(userData: RegisterOAuthData): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/auth/register-oauth`, userData)
      .pipe(
        tap((response) => {
          localStorage.setItem(this.TOKEN_KEY, response.token);
          this.storeUserData(response.user);

          this.tokenSubject.next(response.token);
          this.currentUserSubject.next(response.user);
          this.isLoggedInSubject.next(true);
          this.startProactiveTokenRefresh();
        }),
        catchError((error) => {
          let errorMsg = 'Registration failed';
          if (error.status === 409) {
            errorMsg = 'Email already registered';
          } else if (error.error && error.error.message) {
            errorMsg = error.error.message;
          }
          return throwError(() => new Error(errorMsg));
        })
      );
  }

  verifyEmail(data: VerifyEmailData): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/auth/verify-email`, data).pipe(
      catchError((error) => {
        let errorMsg = 'Email verification failed';
        if (error.error && error.error.message) {
          errorMsg = error.error.message;
        }
        return throwError(() => new Error(errorMsg));
      })
    );
  }

  login(userData: LoginUserData): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/auth/login`, userData, {
        withCredentials: true,
      })
      .pipe(
        tap((response) => {
          localStorage.setItem(this.TOKEN_KEY, response.token);
          this.storeUserData(response.user);

          this.tokenSubject.next(response.token);
          this.currentUserSubject.next(response.user);
          this.isLoggedInSubject.next(true);
          this.startProactiveTokenRefresh();
        }),
        catchError((error) => {
          let errorMsg = 'Login failed';
          if (error.status === 401) {
            errorMsg = 'Invalid email or password';
          } else if (error.error && error.error.message) {
            errorMsg = error.error.message;
          }
          return throwError(() => new Error(errorMsg));
        })
      );
  }

  refreshToken(): Observable<RefreshTokenResponse> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => of({ status: 'success', token: token! }))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.http
      .post<RefreshTokenResponse>(
        `${this.apiUrl}/auth/refresh-token`,
        {},
        {
          withCredentials: true,
        }
      )
      .pipe(
        tap((response) => {
          if (response.status === 'success' && response.token) {
            localStorage.setItem(this.TOKEN_KEY, response.token);
            this.tokenSubject.next(response.token);
            this.isLoggedInSubject.next(true);
            this.refreshTokenSubject.next(response.token);
          }
        }),
        catchError((error) => {
          console.error('Token refresh failed:', error);
          this.clearAuthData();
          return throwError(() => new Error('Token refresh failed'));
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
  }

  logout(): Observable<any> {
    const token = this.getToken();

    if (!token) {
      this.clearAuthData();
      return of({ success: true });
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    return this.http
      .post<{ message: string; success: boolean }>(
        `${this.apiUrl}/auth/logout`,
        {},
        {
          headers,
          withCredentials: true,
        }
      )
      .pipe(
        tap((response) => {}),
        catchError((error) => {
          console.error('❌ Backend logout failed:', error);
          return of({ success: false, message: 'Logout completed locally' });
        }),
        finalize(() => {
          this.clearAuthData();
        })
      );
  }

  logoutSync(): void {
    this.logout().subscribe({
      next: (response) => {},
      error: (error) => {
        console.error('Logout error:', error);
      },
    });
  }

  getToken(): string | null {
    return this.tokenSubject.value || localStorage.getItem(this.TOKEN_KEY);
  }

  public setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    this.tokenSubject.next(token);
    this.isLoggedInSubject.next(true);
    this.startProactiveTokenRefresh();
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getCurrentUser(): Observable<User | null> {
    const currentUser = this.currentUserSubject.value;
    if (currentUser) {
      return of(currentUser);
    }

    if (this.isLoggedIn()) {
      return this.forceRefreshUser();
    }

    return of(null);
  }

  forgotPassword(
    email: string,
    recaptchaToken?: string
  ): Observable<{ message: string }> {
    const payload: any = { email };
    if (recaptchaToken) {
      payload.recaptchaToken = recaptchaToken;
    }

    return this.http
      .post<{ message: string }>(`${this.apiUrl}/auth/forgot-password`, payload)
      .pipe(
        catchError((error) => {
          let errorMsg = 'Failed to process password reset request';
          if (error.error && error.error.message) {
            errorMsg = error.error.message;
          }
          return throwError(() => new Error(errorMsg));
        })
      );
  }

  resetPassword(
    token: string,
    newPassword: string,
    recaptchaToken?: string
  ): Observable<{ message: string }> {
    const payload: any = {
      token,
      new_password: newPassword,
    };
    if (recaptchaToken) {
      payload.recaptchaToken = recaptchaToken;
    }

    return this.http
      .post<{ message: string }>(`${this.apiUrl}/auth/reset-password`, payload)
      .pipe(
        catchError((error) => {
          let errorMsg = 'Failed to reset password';
          if (error.status === 400) {
            errorMsg = 'Invalid or expired token';
          } else if (error.error && error.error.message) {
            errorMsg = error.error.message;
          }
          return throwError(() => new Error(errorMsg));
        })
      );
  }

  handlePasswordChangeResponse(response: any): boolean {
    if (response.passwordChanged) {
      alert(
        response.message ||
          'Password changed successfully. You will now be logged out for security.'
      );

      this.clearAuthData();
      window.location.href =
        '/login?message=Please log in again with your new password';

      return true;
    }
    return false;
  }

  updateCurrentUser(user: User): void {
    if (user.balance && typeof user.balance === 'string') {
      user.balance = parseFloat(user.balance);
    }

    this.currentUserSubject.next(user);
    this.storeUserData(user);
  }

  isListingOwner(listingUserId: number): boolean {
    const currentUser = this.currentUserSubject.value;
    return currentUser !== null && currentUser.id === listingUserId;
  }

  isListingOwnerByDetails(listingUserEmail: string): boolean {
    const currentUser = this.currentUserSubject.value;
    return currentUser !== null && currentUser.email === listingUserEmail;
  }

  getCurrentUserId(): number | null {
    const currentUser = this.currentUserSubject.value;
    return currentUser ? currentUser.id : null;
  }

  getCurrentUserSync(): User | null {
    return this.currentUserSubject.value;
  }

  isAdmin(): boolean {
    const currentUser = this.currentUserSubject.value;
    return currentUser !== null && currentUser.is_admin === true;
  }

  isAdminSync(): boolean {
    return this.isAdmin();
  }

  getOAuthToken(): Observable<{ status: string; token: string; user: User }> {
    return this.http
      .get<{ status: string; token: string; user: User }>(
        `${environment.websocketUrl}/auth/oauth-token`,
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error getting OAuth token:', error);
          return throwError(() => new Error('Failed to get OAuth token'));
        })
      );
  }

  hasMarketingConsent(): boolean {
    const currentUser = this.currentUserSubject.value;
    return currentUser?.marketing_consent === true;
  }

  updateMarketingConsent(consent: boolean): void {
    const currentUser = this.currentUserSubject.value;
    if (currentUser) {
      currentUser.marketing_consent = consent;
      this.updateCurrentUser(currentUser);
    }
  }

  public get isRefreshingToken(): boolean {
    return this.isRefreshing;
  }

  public get refreshTokenObservable(): Observable<string | null> {
    return this.refreshTokenSubject.asObservable();
  }
}
